import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, HelpCircle, Mic, Camera, Square, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

/**
 * SmartSearchHelper — Inclusive "describe the problem" assistant.
 * + Voice mic (Whisper)
 * + Photo diagnosis (Vision)
 */
export default function SmartSearchHelper({ query, suburb }) {
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [examples, setExamples] = useState(null);
  const [helper, setHelper] = useState({ title: '', body: '' });
  const debounceRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recognising, setRecognising] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  useEffect(() => {
    api.smartSearchExamples()
      .then((r) => {
        setExamples(r.data.examples || []);
        setHelper({ title: r.data.helper_title, body: r.data.helper_body });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 3) {
      setMatch(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.smartSearch(query);
        setMatch(r.data);
      } catch {
        setMatch(null);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const goSearch = (categoryOrPhrase) => {
    const params = new URLSearchParams();
    params.set('q', categoryOrPhrase);
    if (suburb) params.set('suburb', suburb);
    navigate(`/search?${params.toString()}`);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 500) {
          toast.error('Recording too short — try again.');
          return;
        }
        setRecognising(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'voice.webm');
          const r = await api.voiceTranscribe(fd);
          const { transcript, category_display, category } = r.data;
          if (!transcript) {
            toast.error("Didn't catch that — try again.");
          } else if (category) {
            toast.success(`Heard: "${transcript}" → ${category_display}`);
            goSearch(category);
          } else {
            toast.info(`Heard: "${transcript}"`);
            goSearch(transcript);
          }
        } catch (e) {
          toast.error('Voice transcription failed.');
        } finally {
          setRecognising(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      toast.info('Listening… speak your problem, then tap the stop button.', { duration: 4000 });
    } catch (e) {
      toast.error('Microphone permission denied.');
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state === 'recording') mediaRef.current.stop();
    setRecording(false);
  };

  const showSuggestion = match && match.matched;

  return (
    <div className="mt-6 max-w-4xl" data-testid="smart-search-helper">
      {/* Voice + Photo buttons */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          onClick={recording ? stopRecording : startRecording}
          disabled={recognising}
          className={`inline-flex items-center gap-2 border px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] transition-all ${
            recording ? 'border-[#D96C4E] bg-[#D96C4E] text-white' : 'border-[#1A362D] text-[#1A362D] hover:bg-[#1A362D] hover:text-white'
          }`}
          data-testid="smart-voice-btn"
        >
          {recording ? <><Square size={12} /> Stop &amp; find tradie</> : <><Mic size={14} /> Speak your problem</>}
        </button>
        <button
          onClick={() => setShowPhotoModal(true)}
          className="inline-flex items-center gap-2 border border-[#1A362D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#1A362D] transition-all hover:bg-[#1A362D] hover:text-white"
          data-testid="smart-photo-btn"
        >
          <Camera size={14} /> Snap a photo
        </button>
        {recognising && <span className="text-xs italic text-[#8A8988]">Transcribing…</span>}
      </div>

      {/* Live suggestion */}
      {showSuggestion && (
        <button
          onClick={() => goSearch(match.category)}
          className="group flex w-full items-center justify-between gap-3 border-2 border-[#1A362D] bg-white px-4 py-3 text-left transition-all hover:bg-[#1A362D] hover:text-white"
          data-testid="smart-search-suggestion"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center bg-[#1A362D] text-white group-hover:bg-white group-hover:text-[#1A362D]">
              <Sparkles size={16} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Sounds like you need</div>
              <div className="mt-1 font-heading text-lg font-bold text-[#1C1B1A] group-hover:text-white" data-testid="smart-search-category">
                {match.category_display}
              </div>
              {match.matched_phrase && (
                <div className="mt-0.5 text-xs text-[#8A8988] group-hover:text-white/70">Matched on "{match.matched_phrase}"</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#1A362D] group-hover:text-white">
            <span className="hidden sm:inline">Search {match.category_display}s</span>
            <ArrowRight size={16} />
          </div>
        </button>
      )}

      {/* Helper card */}
      {!showSuggestion && examples && (
        <div className="border border-[#E5E4E0] bg-white p-5" data-testid="smart-search-helper-card">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center bg-[#D96C4E]/[0.12] text-[#D96C4E]">
              <HelpCircle size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading text-base font-bold text-[#1C1B1A]" data-testid="smart-search-helper-title">
                {helper.title || "Not sure what tradie you need?"}
              </div>
              <p className="mt-1 text-sm text-[#5C5B5A]">
                {helper.body || "Just describe the problem in your own words. We'll point you the right way."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {examples.map((ex, i) => (
                  <button key={i} onClick={() => goSearch(ex.category)}
                    className="border border-[#E5E4E0] bg-[#FAF9F6] px-3 py-1.5 text-xs font-medium text-[#1C1B1A] transition-all hover:border-[#1A362D] hover:bg-[#1A362D] hover:text-white"
                    data-testid={`smart-search-example-${i}`}
                  >{ex.phrase}</button>
                ))}
              </div>
              <p className="mt-4 text-[11px] italic text-[#8A8988]">No judgement, no jargon. Everyone starts somewhere.</p>
            </div>
          </div>
        </div>
      )}

      {showPhotoModal && <PhotoDiagnoseModal onClose={() => setShowPhotoModal(false)} onMatch={(cat) => { setShowPhotoModal(false); goSearch(cat); }} />}
    </div>
  );
}

function PhotoDiagnoseModal({ onClose, onMatch }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Image must be under 8 MB.'); return; }
    setFile(f);
    setResult(null);
    const r = new FileReader();
    r.onload = () => setPreview(String(r.result));
    r.readAsDataURL(f);
  };

  const diagnose = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const raw = preview.split(',')[1] || '';
      const r = await api.photoDiagnose({ image_data_b64: raw, mime_type: file.type });
      setResult(r.data);
    } catch (e) {
      toast.error('Photo diagnosis failed — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="photo-diagnose-modal">
      <div className="w-full max-w-lg border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Photo Diagnosis</div>
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">Snap a photo, we'll find the trade</h3>
          </div>
          <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-photo-diagnose"><X size={18} /></button>
        </div>
        <div className="space-y-4 p-6">
          {!preview ? (
            <label className="block cursor-pointer border border-dashed border-[#1A362D] bg-[#FAF9F6] p-8 text-center hover:bg-[#1A362D]/[0.05]" data-testid="photo-dropzone">
              <input type="file" accept="image/*" capture="environment" onChange={pick} className="hidden" data-testid="photo-file-input" />
              <Camera size={28} className="mx-auto text-[#1A362D]" />
              <div className="mt-3 text-sm font-semibold text-[#1C1B1A]">Take a photo or choose one</div>
              <div className="mt-1 text-xs text-[#8A8988]">JPG or PNG, up to 8 MB</div>
            </label>
          ) : (
            <>
              <img src={preview} alt="preview" className="mx-auto max-h-72 w-auto" data-testid="photo-preview" />
              {!result && (
                <button onClick={diagnose} disabled={busy} className="btn-primary w-full justify-center" data-testid="photo-diagnose-submit">
                  {busy ? 'Looking at the photo…' : 'Find the right trade'} <ArrowRight size={14} />
                </button>
              )}
              {result && (
                <div className="border border-[#1A362D] bg-[#1A362D]/[0.05] p-4" data-testid="photo-diagnose-result">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#D96C4E]">
                    Sounds like a job for · {result.confidence} confidence
                  </div>
                  <div className="mt-1 font-heading text-xl font-bold text-[#1C1B1A]">{result.category_display}</div>
                  <p className="mt-2 text-sm text-[#5C5B5A]">{result.summary}</p>
                  <p className="mt-2 text-xs italic text-[#8A8988]">{result.next_step}</p>
                  <button onClick={() => onMatch(result.category)} className="btn-primary mt-4 w-full justify-center" data-testid="photo-goto-search">
                    Search {result.category_display}s <ArrowRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
